import { createContext, useContext, useMemo } from "react";
import { useTenantConfig } from "./api";
import { useAuth } from "./AuthContext";
import { STEP_DEFS, applyStageOverlay, withCustomStages } from "./journey";
import { ROLE_LABEL } from "./roleViews";
import { canSeePage as canSeePageFor, pagesFor as pagesForPerms } from "./access";
import { pluralize } from "@packs/automotive/web/lib/workshopAnalytics";

const DEFAULT_UNIT_LABEL = "Job Card";

// The tenant's name for its job-card stage, read as a noun — "Job Card Created"
// / "Repair Order Sent" both name the same 2.1 unit, so a trailing
// Created/Sent is dropped. Mirrors apps/api/analytics.py's
// unit_label_from_stages, which does the same for the analytics endpoints —
// this is the one place every *other* page reads the unit's name from.
function unitLabelFromStages(stages) {
  const jobcard = (stages || []).find((s) => s.key === "jobcard");
  const label = (jobcard?.label || "").replace(/\s+(created|sent)$/i, "").trim();
  return label || DEFAULT_UNIT_LABEL;
}

// Per-tenant wording. The overlay from the API is sparse: anything a brand has
// not renamed falls back to the definitions shipped in journey.js / roleViews.js,
// so a tenant with no configuration renders exactly as the product always has.
//
// This is presentation only. Stage keys and role codes never change, so nothing
// here affects which column a stage reads or which email a role is invited on.
const TenantConfigContext = createContext(null);

// Long-form role names used before any brand renamed them. ROLE_LABEL holds the
// short forms (GM, WM) that badges and dropdowns use.
export const DEFAULT_ROLE_TERMS = {
  master: { label: "Master", short: "Master" },
  gm: { label: "General Manager", short: "GM" },
  wm: { label: "Workshop Manager", short: "WM" },
  ccm: { label: "Customer Care Manager", short: "CCM" },
  psf: { label: "Post Service Feedback", short: "PSF" },
  receptionist: { label: "Receptionist", short: "Receptionist" },
  sa: { label: "Service Advisor", short: "SA" },
};

export function TenantConfigProvider({ children }) {
  const { session } = useAuth();
  const { data, isLoading } = useTenantConfig(!!session);

  const value = useMemo(() => {
    const stageOverlay = data?.stages || [];
    const termRows = data?.role_terms || [];

    const termsByKey = new Map(termRows.map((t) => [t.role_key, t]));

    // Long name for a role, e.g. "General Manager" -> "Territory Head".
    const roleLabel = (key) =>
      termsByKey.get(key)?.label || DEFAULT_ROLE_TERMS[key]?.label || key;

    // Short/badge form. A brand may rename the long form only, in which case
    // the stored short_label is blank and the built-in short form still applies.
    const roleShort = (key) =>
      termsByKey.get(key)?.short_label ||
      DEFAULT_ROLE_TERMS[key]?.short ||
      ROLE_LABEL[key] ||
      key;

    // Whether this brand turned on Regions management for a role, via the
    // Roles admin page — master/superadmin always have it regardless.
    const canManageRegions = (key) => key === "master" || !!termsByKey.get(key)?.can_manage_regions;

    // Each role's access policy and permissions (Super Admin > Roles & access).
    const roleRows = data?.roles || [];
    const roleAccess = (key) => roleRows.find((r) => r.code === key) || null;
    const canSeePage = (key, page) => canSeePageFor(key, page, roleRows);
    // "edit" | "read" | false for a levelled page; a Master edits everything.
    const pageLevel = (key, page) => {
      if (key === "master") return "edit";
      const row = roleRows.find((r) => r.code === key);
      return row ? pagesForPerms(row.permissions || [])[page] || false : false;
    };

    const stages = applyStageOverlay(withCustomStages(STEP_DEFS, stageOverlay), stageOverlay);
    const stageLabel = (key) =>
      stages.find((s) => s.key === key)?.label ||
      STEP_DEFS.find((s) => s.key === key)?.label ||
      key;

    // "Job Card" for one brand, "Repair Order" for another — every page that
    // names this unit (Overview KPIs, Journey, Workshop Analytics) reads it
    // from here instead of hardcoding either word.
    const unitLabel = unitLabelFromStages(stages);
    const unitLabelPlural = pluralize(unitLabel);

    const featureFlags = data?.tenant?.feature_flags || {};
    // The one file a stage's template carries, {stage: "document" | "image" | "video"}.
    const stageFiles = data?.stage_files || {};

    // Which of the desk's template sends this brand actually has configured,
    // as {key: {available, reason}}. Empty until the config loads, and an
    // unknown key is treated as available — a brand that has configured
    // nothing special behaves exactly as before.
    const deskTemplates = data?.desk_templates || {};
    const templateAvailability = (key) =>
      deskTemplates[key] || { available: true, reason: "" };

    // Whether a nav page is enabled for this brand (default: on once loaded).
    // While the tenant config is still loading, featureFlags is empty and every
    // page would otherwise read as enabled - hide pages until the real flags are
    // in, so a brand that has turned a page off never flashes it in the nav.
    const isPageEnabled = (page) => (isLoading ? false : featureFlags[`page_${page}`] !== false);

    // "maruti" (default) or "hyundai"
    const bookServiceUi = featureFlags.book_service_ui || "maruti";
    const preDeliveryFeedbackChain = featureFlags.pre_delivery_feedback_chain === true;
    // The advisor desk's chat thread and message box (on unless turned off).
    const deskChat = featureFlags.desk_chat !== false;

    return {
      loading: isLoading,
      tenant: data?.tenant || null,
      tenantId: data?.tenant_id ?? null,
      stageOverlay,
      stages,
      stageLabel,
      unitLabel,
      unitLabelPlural,
      roleLabel,
      roleShort,
      canManageRegions,
      roleRows,
      roleAccess,
      canSeePage,
      pageLevel,
      featureFlags,
      stageFiles,
      templateAvailability,
      isPageEnabled,
      bookServiceUi,
      preDeliveryFeedbackChain,
      deskChat,
    };
  }, [data, isLoading]);

  return <TenantConfigContext.Provider value={value}>{children}</TenantConfigContext.Provider>;
}

// Safe before the provider mounts (e.g. on the login screen): falls back to the
// built-in defaults rather than throwing.
export function useTenantTerms() {
  return (
    useContext(TenantConfigContext) || {
      loading: false,
      tenant: null,
      tenantId: null,
      stageOverlay: [],
      stages: STEP_DEFS,
      stageLabel: (key) => STEP_DEFS.find((s) => s.key === key)?.label || key,
      unitLabel: DEFAULT_UNIT_LABEL,
      unitLabelPlural: pluralize(DEFAULT_UNIT_LABEL),
      roleLabel: (key) => DEFAULT_ROLE_TERMS[key]?.label || key,
      roleShort: (key) => DEFAULT_ROLE_TERMS[key]?.short || ROLE_LABEL[key] || key,
      canManageRegions: (key) => key === "master",
      roleRows: [],
      roleAccess: () => null,
      canSeePage: (key, page) => canSeePageFor(key, page, []),
      pageLevel: (key) => (key === "master" ? "edit" : false),
      featureFlags: {},
      stageFiles: {},
      templateAvailability: () => ({ available: true, reason: "" }),
      isPageEnabled: () => true,
      bookServiceUi: "maruti",
      preDeliveryFeedbackChain: false,
      deskChat: true,
    }
  );
}
